import classNames from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';

interface Props {
  children: ReactNode;
  /** Extra classes on the wrapper (layout passthrough). */
  className?: string;
}

type RevealState = 'hidden' | 'instant' | 'animated';

// Fades + slides its children up the first time they scroll into view. Falls
// back to immediately visible when IntersectionObserver is unavailable or the
// user prefers reduced motion. Starts hidden on both server and first client
// render, so it's hydration-safe.
//
// Elements already in view when the page loads reveal WITHOUT the animation:
// on a reload that restores a mid-page scroll position, replaying the
// entrance there reads as the whole page settling ~26px after hydration.
export const Reveal = ({ children, className }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [reveal, setReveal] = useState<RevealState>('hidden');

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    if (typeof IntersectionObserver === 'undefined' || prefersReduced) {
      setReveal('instant');
      return;
    }

    // The observer's first callback reports the state at load — intersecting
    // there means "already in view", not "scrolled into view".
    let initial = true;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setReveal(initial ? 'instant' : 'animated');
          observer.disconnect();
        }
        initial = false;
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={classNames(
        reveal === 'animated' &&
          'transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none',
        reveal === 'hidden'
          ? 'translate-y-[26px] opacity-0'
          : 'translate-y-0 opacity-100',
        className,
      )}
    >
      {children}
    </div>
  );
};
